import React, { useEffect, useState, forwardRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Dialog,
    Slide,
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Box,
    Button,
    TextField,
    Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Langs, { Localize } from "../../../app/lang/langs";
import { showErrorToast } from "../../../app/utils/helpers";
import { getCollabsNames } from "../../../components/vcomp/vcompSlice";
import { setTeamManagers, ctAddTeamManager } from "../../../components/teams/teamsSlice";
import { getManagersProfiles } from "../../../components/permissions/permissionsSlice";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";
const BORDER_RADIUS_INPUT = "14px";

const fieldLabelSx = {
    fontWeight: 700,
    mb: 0.75,
    letterSpacing: "0.04em",
    color: "rgba(0,0,0,0.45)",
    fontSize: "0.72rem",
    textTransform: "uppercase",
};

const glassmorphicTextFieldSx = {
    mb: 2,
    "& .MuiOutlinedInput-root": {
        borderRadius: BORDER_RADIUS_INPUT,
        backgroundColor: "rgba(255,255,255,0.5)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8)",
        transition: TRANSITION_SMOOTH,
        "& fieldset": {
            borderColor: "rgba(255,144,21,0.08)",
            transition: TRANSITION_SMOOTH,
        },
        "&:hover fieldset": {
            borderColor: "rgba(255,144,21,0.2)",
        },
        "&.Mui-focused fieldset": {
            borderColor: "rgba(255,144,21,0.5)",
            boxShadow: "0 0 0 3px rgba(255,144,21,0.1)",
        },
        "&.Mui-focused": {
            backgroundColor: "rgba(255,255,255,0.10)",
        },
    },
    "& input": {
        fontSize: "16px",
        transition: TRANSITION_SMOOTH,
    },
    "& input::placeholder": {
        opacity: 0.45,
    },
};

const glassCardSx = {
    background: "rgba(255,255,255,0.65)",
    backdropFilter: "blur(20px) saturate(1.3)",
    WebkitBackdropFilter: "blur(20px) saturate(1.3)",
    borderRadius: "20px",
    border: "1px solid rgba(255,255,255,0.5)",
    boxShadow: "0 4px 24px rgba(0,0,0,0.04), 0 1px 4px rgba(0,0,0,0.02), inset 0 1px 0 rgba(255,255,255,0.7)",
    p: 2.5,
    mb: 2,
    transition: TRANSITION_SMOOTH,
};

const Transition = forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export default function MobileAddTeamManagersModal({
    visible,
    setVisible,
    prevManagers = [],
    idTeam,
}) {
    const dispatch = useDispatch();
    const collabsNames = useSelector((state) => state.vcomp.collabsNames);
    const managersProfiles = useSelector((state) => state.permissions.assignableProfiles);

    const [selectedManager, setSelectedManager] = useState(null);
    const [managerType, setManagerType] = useState(null);

    /* ---- Side-effects ---- */
    useEffect(() => {
        dispatch(getCollabsNames());
    }, [dispatch]);

    useEffect(() => {
        if (idTeam) {
            dispatch(getManagersProfiles({ idTeam }));
        }
    }, [idTeam]);

    /* ---- Derived data ---- */
    const filteredCollabs = useMemo(() => {
        if (!collabsNames) return [];
        return collabsNames
            .filter(
                (collab) =>
                    !prevManagers.some(
                        (manager) => manager.id_collab === collab.id
                    )
            )
            .map(({ id, nom, prenom, email }) => ({
                id,
                nom,
                prenom,
                email,
                fullName: `${prenom} ${nom}`,
            }));
    }, [collabsNames, prevManagers]);

    /* ---- Handlers ---- */
    const handleClose = () => {
        setVisible(false);
        setSelectedManager(null);
        setManagerType(null);
    };

    const handleAddManager = () => {
        if (!selectedManager || !idTeam || !managerType) return;

        const managerData = {
            id_team: idTeam,
            id_collab: selectedManager.id,
            id_manager_profile: managerType.id,
        };

        dispatch(ctAddTeamManager(managerData))
            .then((actionResponse) => {
                if (ctAddTeamManager.fulfilled.match(actionResponse)) {
                    const newManager = {
                        id: actionResponse.payload.id,
                        id_team: idTeam,
                        id_collab: selectedManager.id,
                        id_manager_profile: managerType.id,
                        type: managerType.name,
                        nom: selectedManager.nom,
                        prenom: selectedManager.prenom,
                        email: selectedManager.email,
                    };
                    dispatch(setTeamManagers([...prevManagers, newManager]));
                    handleClose();
                }
            })
            .catch((error) => {
                console.error("Erreur lors de l'ajout du manager :", error);
                showErrorToast("Erreur lors de l'ajout du manager.");
            });
    };

    /* ---- Render ---- */
    return (
        <Dialog
            fullScreen
            open={!!visible}
            onClose={handleClose}
            TransitionComponent={Transition}
            sx={{
                zIndex: 1500,
                "& .MuiDialog-paper": {
                    background: "var(--surface-ground, #f4f6f8)",
                },
            }}
        >
            {/* Sticky AppBar */}
            <AppBar
                sx={{
                    position: "sticky",
                    top: 0,
                    bgcolor: "rgba(255,255,255,0.82)",
                    color: "text.primary",
                    boxShadow: "0 1px 12px rgba(0,0,0,0.06)",
                    backdropFilter: "blur(24px) saturate(1.5)",
                    WebkitBackdropFilter: "blur(24px) saturate(1.5)",
                    borderBottom: "1px solid rgba(0,0,0,0.06)",
                    zIndex: 10,
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "2px",
                        background: "linear-gradient(90deg, transparent, rgba(255,144,21,0.3), transparent)",
                    },
                }}
            >
                <Toolbar sx={{ minHeight: 56 }}>
                    <IconButton
                        edge="start"
                        onClick={handleClose}
                        sx={{ mr: 1, color: "text.primary" }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography
                        variant="h6"
                        sx={{
                            flex: 1,
                            fontWeight: 700,
                            fontSize: "1.1rem",
                            letterSpacing: "-0.02em",
                        }}
                    >
                        <Langs str="ADD_MANAGER" />
                    </Typography>
                </Toolbar>
            </AppBar>

            {/* Scrollable content */}
            <Box
                sx={{
                    flex: 1,
                    overflow: "auto",
                    px: 2,
                    py: 2.5,
                    mt: 0,
                    pb: "100px",
                }}
            >
                <Box sx={glassCardSx}>
                    {/* Collaborator picker */}
                    <Typography sx={fieldLabelSx}>
                        <Langs str="SELECT_COLLAB" />
                    </Typography>
                    <Autocomplete
                        value={selectedManager}
                        onChange={(_, newVal) => setSelectedManager(newVal)}
                        options={filteredCollabs}
                        getOptionLabel={(option) => option.fullName || ""}
                        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={Localize('SELECT_COLLAB_PLACEHOLDER')}
                                sx={glassmorphicTextFieldSx}
                                size="small"
                            />
                        )}
                        componentsProps={{
                            popper: { sx: { zIndex: 1600 } },
                        }}
                        sx={{ mb: 1 }}
                        noOptionsText="Aucun collaborateur disponible"
                    />

                    {/* Manager type */}
                    <Typography sx={fieldLabelSx}>
                        <Langs str="MANAGER_TYPE" />
                    </Typography>
                    <Autocomplete
                        value={managerType}
                        onChange={(_, newVal) => { if (newVal) setManagerType(newVal); }}
                        options={managersProfiles}
                        getOptionLabel={(option) => option.name || ""}
                        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                        disableClearable
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={Localize('SELECT_PROFILE_PLACEHOLDER')}
                                sx={glassmorphicTextFieldSx}
                                size="small"
                            />
                        )}
                        componentsProps={{
                            popper: { sx: { zIndex: 1600 } },
                        }}
                        noOptionsText="Aucun profil disponible"
                    />
                </Box>
            </Box>

            {/* Sticky bottom action bar */}
            <Box
                sx={{
                    position: "sticky",
                    bottom: 0,
                    px: 2,
                    py: 2,
                    bgcolor: "rgba(255,255,255,0.88)",
                    backdropFilter: "blur(24px) saturate(1.4)",
                    WebkitBackdropFilter: "blur(24px) saturate(1.4)",
                    borderTop: "1px solid rgba(255,255,255,0.5)",
                    boxShadow: "0 -4px 20px rgba(0,0,0,0.06)",
                    zIndex: 10,
                }}
            >
                <Button
                    fullWidth
                    variant="contained"
                    onClick={handleAddManager}
                    disabled={!selectedManager}
                    sx={{
                        borderRadius: "999px",
                        minHeight: 48,
                        fontWeight: 700,
                        textTransform: "none",
                        fontSize: "1rem",
                        background: "linear-gradient(135deg, #FF9015 0%, #FFC100 100%)",
                        "&:hover": { background: "linear-gradient(135deg, #FFC100 0%, #FF9015 100%)" },
                        "&:active": { transform: "scale(0.97)" },
                        boxShadow: "0 4px 16px rgba(255,144,21,0.35), 0 1px 3px rgba(255,144,21,0.2)",
                        "&.Mui-disabled": {
                            bgcolor: "rgba(255,144,21,0.4)",
                            color: "rgba(255,255,255,0.7)",
                        },
                    }}
                >
                    <Langs str="ADD" />
                </Button>
            </Box>
        </Dialog>
    );
}
